import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { MaskLine, FadeUp, EASE } from '@/components/Reveal';
import { Marquee } from '@/components/Marquee';
import { CaseModal } from '@/components/CaseModal';
import { ReelPlayer } from '@/components/ReelPlayer';
import { ButterPan } from '@/components/ButterPan';
import { WORK, CLIENTS, REELS, SOCIALS } from '@/data/content';

export default function Work() {
  const [active, setActive] = useState(null);

  return (
    <main data-testid="work-page">
      {/* HERO */}
      <section className="px-6 sm:px-12 pt-40 pb-10 sm:pb-14">
        <div className="max-w-[1500px] mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-8"
          >
            Selected Work & Case Studies
          </motion.p>
          <h1 data-testid="work-headline" className="font-slab font-black leading-[0.98] tracking-tight text-[11vw] sm:text-[8.5vw] lg:text-[6.8vw]">
            <span className="relative inline-block">
              <MaskLine delay={0.2}>FRESH FROM</MaskLine>
              <motion.span
                initial={{ opacity: 0, y: 20, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -4 }}
                transition={{ delay: 0.95, duration: 0.7, ease: EASE }}
                className="font-script text-royal absolute -top-[0.62em] right-[-0.3em] text-[0.68em] pointer-events-none select-none"
              >
                golden
              </motion.span>
            </span>
            <MaskLine delay={0.34}>THE STUDIO.</MaskLine>
          </h1>
        </div>
      </section>

      {/* BUTTER PAN */}
      <section className="px-6 sm:px-12 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
          className="max-w-[1500px] mx-auto"
        >
          <ButterPan works={WORK} onOpenCase={setActive} />
        </motion.div>
      </section>

      {/* REELS */}
      <section data-testid="reels-section" className="px-6 sm:px-12 pb-24">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-4">Fresh from the feed</p>
              <h2 className="font-slab font-black text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                Watch the work<span className="text-royal">.</span>
              </h2>
            </div>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="reels-follow-link"
              className="group inline-flex items-center gap-2 font-slab font-bold border-b-2 border-ink pb-0.5 hover:text-royal hover:border-royal transition-colors"
            >
              Follow @thebuttermedia
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {REELS.map((r, i) => (
              <FadeUp key={r.id} delay={i * 0.08} className="h-full">
                <div data-testid={`reel-card-${r.id}`} className="w-full h-full flex flex-col bg-ink border border-ink rounded-[1.5rem] overflow-hidden shadow-[8px_8px_0_#1A1A1A]">
                  <ReelPlayer reel={r} title={r.label} className="relative aspect-[9/16] max-h-[640px]" />
                  <div className="flex items-center justify-between px-5 py-4 text-cream">
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold">{r.label}</span>
                    <a href={r.href} target="_blank" rel="noreferrer" data-testid={`reel-open-${r.id}`} className="text-cream/70 hover:text-butter transition-colors">
                      <ArrowUpRight size={18} />
                    </a>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT ROSTER */}
      <section data-testid="client-roster" className="py-16 border-t border-ink/10">
        <FadeUp>
          <p className="text-center text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-10 px-6">
            Our Growing Client Family
          </p>
        </FadeUp>
        <Marquee
          items={CLIENTS}
          testid="client-marquee"
          duration={55}
          className="py-3 font-slab font-bold text-2xl sm:text-3xl text-ink"
        />
        <Marquee
          items={[...CLIENTS].reverse()}
          reverse
          testid="client-marquee-reverse"
          duration={60}
          className="py-3 font-slab font-bold text-2xl sm:text-3xl text-ink/25"
        />
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 pt-16">
        <FadeUp className="max-w-[1500px] mx-auto">
          <Link
            to="/contact"
            data-testid="work-cta-banner"
            className="group block bg-butter border border-ink rounded-[2rem] px-8 py-16 sm:p-20 text-center shadow-[8px_8px_0_#1A1A1A] hover:shadow-none hover:translate-x-2 hover:translate-y-2 transition-all duration-300"
          >
            <p className="font-script text-royal text-4xl sm:text-5xl mb-4 -rotate-2 inline-block">your brand next?</p>
            <h2 className="font-slab font-black text-4xl sm:text-6xl leading-[1.0] tracking-tight">
              Let’s add your name<br />to the family.
            </h2>
            <span className="mt-10 inline-flex items-center gap-3 font-slab font-bold text-lg border-b-2 border-ink pb-1">
              Get in touch
              <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform duration-300" />
            </span>
          </Link>
        </FadeUp>
      </section>

      <CaseModal work={active} onClose={() => setActive(null)} />
    </main>
  );
}
